<template>
  <div class="bg-white rounded-lg shadow-lg p-4">
    <h2 class="text-xl font-semibold mb-4">Cámaras ({{ cameras.length }})</h2>
    <div class="space-y-2">
      <div
        v-for="camera in cameras"
        :key="camera.id"
        :class="[
          'flex flex-col p-3 rounded-lg border',
          camera.isActive ? 'border-[#18223B] bg-[#18223B]/5' : 'border-gray-200',
          activeCamera?.id === camera.id ? 'ring-2 ring-[#F6A11B]' : ''
        ]"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <button
              @click="$emit('toggle', camera.id)"
              :class="[
                'p-2 rounded-full',
                camera.isActive ? 'text-[#18223B] hover:bg-[#18223B]/10' : 'text-gray-400 hover:bg-gray-100'
              ]"
            >
              <Eye v-if="camera.isActive" :size="20" />
              <EyeOff v-else :size="20" />
            </button>
            <div>
              <p class="font-medium">Cámara {{ camera.id.slice(0, 4) }}</p>
              <p class="text-sm text-gray-500">
                {{ camera.height }}m • {{ camera.tilt }}° • {{ camera.fov }}°
              </p>
            </div>
          </div>
          <div class="flex space-x-2">
            <button
              @click="$emit('select', {...camera})"
              class="p-2 text-[#18223B] hover:bg-[#18223B]/10 rounded-full"
            >
              <Settings :size="20" />
            </button>
            <button
              @click="$emit('delete', camera.id)"
              class="p-2 text-red-600 hover:bg-red-50 rounded-full"
            >
              <Trash2 :size="20" />
            </button>
          </div>
        </div>
        
        <!-- Settings panel when camera is selected -->
        <div v-if="activeCamera?.id === camera.id" class="mt-4 space-y-4 border-t pt-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Altura (m)
            </label>
            <input
              type="number"
              :value="camera.height"
              @input="updateCameraProperty(camera, 'height', $event)"
              min="0.5"
              max="400"
              step="0.5"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F6A11B]"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Inclinación (°)
            </label>
            <input
              type="range"
              :value="camera.tilt"
              @input="updateCameraProperty(camera, 'tilt', $event)"
              min="-60"
              max="20"
              step="1"
              class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#F6A11B]"
            />
            <div class="mt-1 text-sm text-gray-500">{{ camera.tilt }}°</div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Campo de Visión (FOV)
            </label>
            <input
              type="range"
              :value="camera.fov"
              @input="updateCameraProperty(camera, 'fov', $event)"
              min="40"
              max="110"
              step="1"
              class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#F6A11B]"
            />
            <div class="mt-1 text-sm text-gray-500">{{ camera.fov }}°</div>
          </div>
        </div>
      </div>
      <p v-if="cameras.length === 0" class="text-center text-gray-500 py-4">
        No hay cámaras añadidas. Haz clic en el mapa para añadir una cámara.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Eye, EyeOff, Trash2, Settings } from 'lucide-vue-next';
import { Camera } from '../types';

const props = defineProps<{
  cameras: Camera[];
  activeCamera: Camera | null;
}>();

const emit = defineEmits<{
  (e: 'delete', id: string): void;
  (e: 'toggle', id: string): void;
  (e: 'select', camera: Camera): void;
  (e: 'update', camera: Camera): void;
}>();

function updateCameraProperty(camera: Camera, property: keyof Camera, event: Event) {
  const target = event.target as HTMLInputElement;
  const value = target.type === 'number' || target.type === 'range' 
    ? parseFloat(target.value) 
    : target.value;
  
  const updatedCamera = {
    ...camera,
    [property]: value
  };
  
  emit('update', updatedCamera);
}
</script>